import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import EnhancedSectionEditor from './components/EnhancedSectionEditor';
import SideNavigation from './components/SideNavigation';
import GlobalDashboard from './components/GlobalDashboard';
import BulkEditModal from './components/BulkEditModal';
import QuickInspection from './components/QuickInspection';
import ProjectsList from './components/ProjectsList';
import ProjectDetail from './components/ProjectDetail';
import CreateProject from './components/CreateProject';
import CommunesList from './components/CommunesList';
import CommuneDetail from './components/CommuneDetail';
import { API_URL } from './config';

declare global {
  interface Window {
    autoSaveTimeout?: NodeJS.Timeout;
  }
}

interface RoadSection {
  id?: number;
  numero: number;
  libelle: string;
  longueur: number;
  largeur: number;
  trafic: string;
  structure: string;
  construction_date?: string;
  surface_glissante: number;
  degradation_revetement: number;
  fissures_longitudinales: number;
  fissures_transversales: number;
  nids_poule: number;
  deformation: number;
  deflection_t1?: number;
  deflection_t2?: number;
  deflection_t3?: number;
  deflection_t4?: number;
  deflection_t5?: number;
  photos?: string;
}

interface CalculationResults {
  nombre_troncons: number;
  longueur_totale: number;
  surface_totale: number;
  note_visuelle_i1: number;
  note_structurelle_i5: number;
  note_chaussee: number;
  repartition_trafic: Record<string, number>;
  diagnostic_degradation: Record<string, number>;
  diagnostic_structurel: Record<string, any>;
}

function App() {
  const [sections, setSections] = useState<RoadSection[]>([]);
  const [selectedRoadId, setSelectedRoadId] = useState<number | null>(null);
  const [selectedRoadName, setSelectedRoadName] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [selectedCommuneId, setSelectedCommuneId] = useState<number | null>(null);
  const [selectedCommuneName, setSelectedCommuneName] = useState('');
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showQuickInspection, setShowQuickInspection] = useState(false);
  const [preselectedCommuneId, setPreselectedCommuneId] = useState<number | undefined>();
  const [preselectedCommuneName, setPreselectedCommuneName] = useState<string | undefined>();

  const loadSections = async (roadId: number) => {
    try {
      const response = await fetch(`${API_URL}/roads/${roadId}/sections`);
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  const handleCalculate = async () => {
    if (!selectedRoadId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/roads/${selectedRoadId}/calculate`, {
        method: 'POST',
      });
      
      const result = await response.json();
      setResults(result);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSection = async () => {
    if (!selectedRoadId) return;
    
    const newSection = {
      numero: sections.length + 1,
      libelle: `Tronçon ${sections.length + 1}`,
      longueur: 50,
      largeur: 5.5,
      trafic: 'T1',
      structure: 'E',
      construction_date: new Date().toISOString().split('T')[0],
      surface_glissante: 0,
      degradation_revetement: 0,
      fissures_longitudinales: 0,
      fissures_transversales: 0,
      nids_poule: 0,
      deformation: 0,
      deflection_t1: null,
      deflection_t2: null,
      deflection_t3: null,
      deflection_t4: null,
      deflection_t5: null
    };
    
    try {
      const response = await fetch(`${API_URL}/roads/${selectedRoadId}/sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSection),
      });
      
      if (response.ok) {
        loadSections(selectedRoadId);
      }
    } catch (error) {
      console.error('Error adding section:', error);
    }
  };

  const updateSection = async (sectionId: number, field: keyof RoadSection, value: any) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const updatedSection = { ...section, [field]: value };
    const { id, ...sectionData } = updatedSection;
    
    // Update local state immediately
    setSections(prevSections => 
      prevSections.map(s => 
        s.id === sectionId ? updatedSection : s
      )
    );
    
    // Auto-save with debounce
    if (window.autoSaveTimeout) {
      clearTimeout(window.autoSaveTimeout);
    }
    
    window.autoSaveTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/sections/${sectionId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sectionData),
        });
        
        if (!response.ok) {
          loadSections(selectedRoadId!);
        }
      } catch (error) {
        console.error('Error updating section:', error);
        if (selectedRoadId) loadSections(selectedRoadId);
      }
    }, 1000);
  };

  const deleteSection = async (sectionId: number) => {
    try {
      const response = await fetch(`${API_URL}/sections/${sectionId}`, {
        method: 'DELETE',
      });
      
      if (response.ok && selectedRoadId) {
        loadSections(selectedRoadId);
      }
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const handleSelectCommune = (communeId: number, communeName: string) => {
    setSelectedCommuneId(communeId);
    setSelectedCommuneName(communeName);
    setActiveView('commune-detail');
  };

  const handleSelectProject = (projectId: number, projectName: string) => {
    setSelectedProjectId(projectId);
    setSelectedProjectName(projectName);
    setActiveView('project-detail');
  };

  const handleSelectRoad = (roadId: number, roadName: string) => {
    setSelectedRoadId(roadId);
    setSelectedRoadName(roadName);
    loadSections(roadId);
    setResults(null);
    setActiveView('sections');
  };

  const handleCreateProject = (projectId: number) => {
    setPreselectedCommuneId(undefined);
    setPreselectedCommuneName(undefined);
    handleSelectProject(projectId, '');
  };

  const handleViewChange = (view: string) => {
    setActiveView(view);
    if (view === 'communes') {
      setSelectedRoadId(null);
      setSelectedRoadName('');
    }
  };

  return (
    <div className="App">
      <SideNavigation 
        activeView={activeView}
        onViewChange={handleViewChange}
        selectedCommune={selectedCommuneName}
        selectedProject={selectedProjectName}
        selectedRoad={selectedRoadName}
      />
      
      <main className="main-content">
        {activeView === 'dashboard' && (
          <GlobalDashboard 
            onViewProjects={() => setActiveView('projects')}
            onViewCommunes={() => setActiveView('communes')}
            onCreateProject={() => setActiveView('create-project')}
          />
        )}
        
        {activeView === 'projects' && (
          <div className="projects-view">
            <div className="page-header">
              <h1>📁 Gestion des Projets</h1>
              <button onClick={() => setActiveView('create-project')} className="btn-primary">
                ➕ Nouveau Projet
              </button>
            </div>
            <ProjectsList onSelectProject={handleSelectProject} />
          </div>
        )}
        
        {activeView === 'project-detail' && selectedProjectId && (
          <ProjectDetail 
            projectId={selectedProjectId}
            onSelectRoad={handleSelectRoad}
          />
        )}
        
        {activeView === 'create-project' && (
          <CreateProject
            onClose={() => {
              setActiveView(preselectedCommuneId ? 'commune-detail' : 'dashboard');
              setPreselectedCommuneId(undefined);
              setPreselectedCommuneName(undefined);
            }}
            onSuccess={handleCreateProject}
            preselectedCommuneId={preselectedCommuneId}
            preselectedCommuneName={preselectedCommuneName}
          />
        )}
        
        {activeView === 'communes' && (
          <div className="communes-view">
            <h1>🏘️ Communes</h1>
            <CommunesList onSelectCommune={handleSelectCommune} />
          </div>
        )}
        
        {activeView === 'commune-detail' && selectedCommuneId && (
          <CommuneDetail
            communeId={selectedCommuneId}
            communeName={selectedCommuneName}
            onSelectProject={handleSelectProject}
            onCreateProject={(communeId, communeName) => {
              setPreselectedCommuneId(communeId);
              setPreselectedCommuneName(communeName);
              setActiveView('create-project');
            }}
          />
        )}
        
        {selectedRoadId && activeView === 'sections' && (
          <div className="sections-view">
            <div className="page-header">
              <h1>📝 Gestion des Tronçons - {selectedRoadName}</h1>
              <div className="header-actions">
                <button onClick={() => setShowQuickInspection(true)} className="quick-inspect-btn">
                  🚀 Inspection Rapide
                </button>
                <button onClick={() => setShowBulkEdit(true)} className="bulk-edit-btn">
                  ✏️ Modification en Masse
                </button>
                <button onClick={addSection} className="add-section-btn">
                  ➕ Ajouter Tronçon
                </button>
              </div>
            </div>
            
            {showBulkEdit && (
              <BulkEditModal
                roadId={selectedRoadId}
                onClose={() => setShowBulkEdit(false)}
                onSuccess={() => loadSections(selectedRoadId)}
              />
            )}
            
            {showQuickInspection && sections.length > 0 && (
              <QuickInspection
                sections={sections}
                onUpdate={updateSection}
                onClose={() => setShowQuickInspection(false)}
              />
            )}

            <div className="sections-container collapsible">
              <button 
                className="collapse-toggle"
                onClick={(e) => e.currentTarget.parentElement?.classList.toggle('collapsed')}
              >
                <span className="toggle-icon">▼</span>
                <span className="toggle-text">Tronçons ({sections.length})</span>
              </button>
              
              <div className="sections-content">
                {sections.map((section) => (
                  <EnhancedSectionEditor
                    key={section.id}
                    section={section}
                    onUpdate={(field, value) => section.id && updateSection(section.id, field, value)}
                    onDelete={() => section.id && deleteSection(section.id)}
                  />
                ))}

                {sections.length === 0 && (
                  <div className="empty-sections">
                    <p>🛣️ Aucun tronçon défini</p>
                    <button onClick={addSection} className="btn-primary">
                      Créer le premier tronçon
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedRoadId && activeView === 'road-dashboard' && (
          <Dashboard roadId={selectedRoadId} roadName={selectedRoadName} />
        )}
      </main>
    </div>
  );
}

export default App;